import React from 'react';
import { useTranslation } from 'react-i18next';
import { getCategoriesSorted, getSubcategories, getSubcategoriesV2, CONDITIONS, conditionKey } from '../../data/categories';
import { STATES, getCitiesForState } from '../../data/locations';
import PriceRangeFilter from './PriceRangeFilter';
import RecentSearches from './RecentSearches';
import CategoryExclusionFilter from './CategoryExclusionFilter';
import './FilterSidebar.css';

function FilterSidebar({ filters, onChange }) {
  const { t } = useTranslation();
  const categoriesList = getCategoriesSorted();
  
  // Определяем, использовать старую или новую структуру категорий
  const useV2Categories = filters.useV2Categories || false;
  
  // Получаем подкатегории в зависимости от версии
  const subcategoriesList = useV2Categories 
    ? getSubcategoriesV2(filters.category)
    : getSubcategories(filters.category);

  return (
    <div className="filterSidebar">
      <RecentSearches />
      <h3 className="filterSidebarTitle">{t('search.filters')}</h3>

      <div className="filterGroup">
        <label>{t('search.category')}</label>
        <select
          value={filters.category || ''}
          onChange={(e) =>
            onChange({ 
              ...filters, 
              category: e.target.value, 
              subcategory: '',
              subcategoriesV2: [],
              useV2Categories: false // Сбрасываем флаг при смене категории
            })
          }
          className="filterSelect"
        >
          <option value="">{t('common.all')}</option>
          {categoriesList.map((cat) => (
            <option key={cat.id} value={cat.name}>
              {t(`categories.${cat.id}`)}
            </option>
          ))}
        </select>
      </div>

      {subcategoriesList.length > 0 && (
        <div className="filterGroup">
          <label>{t('search.subcategory')}</label>
          {useV2Categories ? (
            // V2: Множественный выбор подкатегорий
            <div className="subcategoryCheckboxes">
              {subcategoriesList.map((sub) => (
                <label key={sub.id} className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={(filters.subcategoriesV2 || []).includes(sub.id)}
                    onChange={(e) => {
                      const currentSubcategories = filters.subcategoriesV2 || [];
                      const newSubcategories = e.target.checked
                        ? [...currentSubcategories, sub.id]
                        : currentSubcategories.filter(id => id !== sub.id);
                      onChange({ ...filters, subcategoriesV2: newSubcategories });
                    }}
                  />
                  {t(`subcategories.${sub.id}`)}
                </label>
              ))}
            </div>
          ) : (
            // V1: Одиночный выбор
            <select
              value={filters.subcategory || ''}
              onChange={(e) => onChange({ ...filters, subcategory: e.target.value })}
              className="filterSelect"
            >
              <option value="">{t('common.all')}</option>
              {subcategoriesList.map((sub) => (
                <option key={typeof sub === 'string' ? sub : sub.id} value={typeof sub === 'string' ? sub : sub.id}>
                  {t(`subcategories.${typeof sub === 'string' ? sub.toLowerCase().replace(/[^a-z0-9]/g, '-') : sub.id}`)}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <PriceRangeFilter
        minPrice={filters.minPrice}
        maxPrice={filters.maxPrice}
        onChange={(minPrice, maxPrice) =>
          onChange({ ...filters, minPrice, maxPrice })
        }
      />

      <div className="filterGroup">
        <label>{t('search.locationState')}</label>
        <select
          value={filters.state || ''}
          onChange={(e) =>
            onChange({ ...filters, state: e.target.value, city: '' })
          }
          className="filterSelect"
        >
          <option value="">{t('common.all')}</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {filters.state && (
        <div className="filterGroup">
          <label>{t('search.city')}</label>
          <select
            value={filters.city || ''}
            onChange={(e) => onChange({ ...filters, city: e.target.value })}
            className="filterSelect"
          >
            <option value="">{t('common.all')}</option>
            {getCitiesForState(filters.state).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="filterGroup">
        <label>{t('search.condition')}</label>
        <select
          value={filters.condition || ''}
          onChange={(e) => onChange({ ...filters, condition: e.target.value })}
          className="filterSelect"
        >
          <option value="">{t('common.all')}</option>
          {CONDITIONS.map((c) => (
            <option key={c} value={c}>
              {t(`conditions.${conditionKey(c)}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="filterGroup">
        <label>{t('search.adType')}</label>
        <select
          value={filters.adType || ''}
          onChange={(e) => onChange({ ...filters, adType: e.target.value })}
          className="filterSelect"
        >
          <option value="">{t('common.all')}</option>
          <option value="for_sale">{t('adTypes.forSale')}</option>
          <option value="for_rent">{t('adTypes.forRent')}</option>
          <option value="wanted">{t('adTypes.wanted')}</option>
          <option value="exchange">{t('adTypes.exchange')}</option>
          <option value="free">{t('adTypes.free')}</option>
        </select>
      </div>

      <div className="filterGroup">
        <label>
          <input
            type="checkbox"
            checked={!!filters.deliveryAvailable}
            onChange={(e) => onChange({ ...filters, deliveryAvailable: e.target.checked })}
          />
          {t('search.deliveryAvailable')}
        </label>
      </div>

      <div className="filterGroup">
        <label>
          <input
            type="checkbox"
            checked={!!filters.warrantyAvailable}
            onChange={(e) => onChange({ ...filters, warrantyAvailable: e.target.checked })}
          />
          {t('search.warrantyAvailable')}
        </label>
      </div>

      <div className="filterGroup">
        <label>
          <input
            type="checkbox"
            checked={!!filters.verifiedSeller}
            onChange={(e) => onChange({ ...filters, verifiedSeller: e.target.checked })}
          />
          {t('search.verifiedSellerOnly')}
        </label>
      </div>

      <div className="filterGroup">
        <label>{t('search.datePosted')}</label>
        <select
          value={filters.datePosted || ''}
          onChange={(e) => onChange({ ...filters, datePosted: e.target.value })}
          className="filterSelect"
        >
          <option value="">{t('search.anyTime')}</option>
          <option value="today">{t('search.today')}</option>
          <option value="7">{t('search.last7Days')}</option>
          <option value="30">{t('search.last30Days')}</option>
        </select>
      </div>

      <CategoryExclusionFilter
        excludedCategories={filters.excludedCategories || []}
        onToggleExclusion={(subcategory, clearAll) => {
          if (clearAll) {
            onChange({ ...filters, excludedCategories: [] });
          } else if (subcategory) {
            const currentExcluded = filters.excludedCategories || [];
            const newExcluded = currentExcluded.includes(subcategory)
              ? currentExcluded.filter(c => c !== subcategory)
              : [...currentExcluded, subcategory];
            onChange({ ...filters, excludedCategories: newExcluded });
          }
        }}
      />
    </div>
  );
}

export default FilterSidebar;